import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Avatar, Menu, MenuTrigger, MenuPopover, MenuList, MenuItem, MenuDivider, MenuGroup, MenuGroupHeader,
  Persona, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { SignOut20Regular, People20Regular, ArrowUpload20Regular, ArrowDownload20Regular, ArrowImport20Regular } from '@fluentui/react-icons';
import { ImportCsvDialog } from './ImportCsvDialog';
import { ShareAccountDialog } from './ShareAccountDialog';
import { ManageUsersDialog } from './ManageUsersDialog';
import { api } from '../services/api';

interface UserProfileMenuProps {
  accountId?: number | null;
  accountName?: string;
  isOwner?: boolean;
  isAdmin?: boolean;
  onImported?: (rows: any[]) => void;
}

const useStyles = makeStyles({
  trigger: {
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: '0',
  },
  personaContainer: {
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
  },
});

export const UserProfileMenu: React.FC<UserProfileMenuProps> = ({ accountId, accountName, isOwner, isAdmin, onImported }) => {
  const { instance, accounts } = useMsal();
  const styles = useStyles();
  const [photoUrl, setPhotoUrl] = useState<string | undefined>(undefined);
  const [importOpen, setImportOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [usersOpen, setUsersOpen] = useState(false);

  const account = accounts[0];
  const displayName = account?.name || 'User';
  const email = (account as any)?.username || '';

  const idTokenClaims = account?.idTokenClaims as any;
  const roles: string[] = idTokenClaims?.roles || [];
  const role = roles.includes('Admin') ? 'Admin' : 'User';

  useEffect(() => {
    async function fetchPhoto() {
      try {
        const response = await fetch('/api/me/photo', {
          headers: { Authorization: `Bearer ${(account as any)?.idToken || ''}` },
        });
        if (response.ok) {
          const blob = await response.blob();
          setPhotoUrl(URL.createObjectURL(blob));
        }
      } catch {
        // No photo available
      }
    }
    if (account) fetchPhoto();
  }, [account]);

  const handleLogout = () => {
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin }).catch(console.error);
    } else {
      instance.logoutPopup().catch(console.error);
    }
  };

  const handleExport = () => {
    if (accountId) api.exportTransactions(accountId, accountName || 'account');
  };

  const handleDownloadTemplate = () => {
    if (accountId) api.downloadTemplate(accountId);
  };

  return (
    <>
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <button className={styles.trigger} title={displayName}>
            <Avatar
              name={displayName}
              image={photoUrl ? { src: photoUrl } : undefined}
              size={32}
              color="colorful"
            />
          </button>
        </MenuTrigger>
        <MenuPopover>
          <div className={styles.personaContainer}>
            <Persona
              name={displayName}
              secondaryText={email}
              tertiaryText={`Role: ${role}`}
              size="medium"
              avatar={{
                image: photoUrl ? { src: photoUrl } : undefined,
                color: 'colorful',
              }}
            />
          </div>
          <MenuDivider />
          {accountId && (
            <>
              <MenuGroup>
                <MenuGroupHeader>Account</MenuGroupHeader>
                {isOwner && (
                  <MenuItem icon={<People20Regular />} onClick={() => setShareOpen(true)}>
                    Share Account
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem icon={<People20Regular />} onClick={() => setUsersOpen(true)}>
                    Manage Users
                  </MenuItem>
                )}
              </MenuGroup>
              <MenuDivider />
              <MenuGroup>
                <MenuGroupHeader>Data</MenuGroupHeader>
                <MenuItem icon={<ArrowUpload20Regular />} onClick={handleExport}>
                  Export CSV
                </MenuItem>
                <MenuItem icon={<ArrowDownload20Regular />} onClick={handleDownloadTemplate}>
                  Download Template
                </MenuItem>
                <MenuItem icon={<ArrowImport20Regular />} onClick={() => setImportOpen(true)}>
                  Import CSV
                </MenuItem>
              </MenuGroup>
              <MenuDivider />
            </>
          )}
          <MenuList>
            <MenuItem icon={<SignOut20Regular />} onClick={handleLogout}>
              Sign Out
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
      {accountId && onImported && (
        <ImportCsvDialog
          accountId={accountId}
          open={importOpen}
          onClose={() => setImportOpen(false)}
          onImported={onImported}
        />
      )}
      {isOwner && accountId && (
        <ShareAccountDialog
          accountId={accountId}
          accountName={accountName || 'Account'}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
      {isAdmin && (
        <ManageUsersDialog
          open={usersOpen}
          onClose={() => setUsersOpen(false)}
        />
      )}
    </>
  );
};
